import React, { useCallback, useEffect, useState } from 'react';
import {
	DataGrid,
	error,
	StatusType,
	StylovyzeCard,
	Text,
	GridLogicOperator,
	GridFilterModel,
	GridColDef,
} from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import { useDispatch } from 'react-redux';
import { useAssetTypeSummary } from '@Hooks/assetSchemaHooks';
import { selectFailureDefinitionRunResults, selectFailureDefinitionModelPaginationState } from '@Selectors';
import {
	getFailureDefinitionAction,
	getFailureDefinitionRunResults,
	setFailureDefinitionPaginationState,
} from '@Actions/failureDefinition.actions';
import { Box, Link, Stack, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { FailureDefinition, FailureDefinitionRunResult, Filter, GetFailureDefinitionRunResultsRequest } from '@Types';
import { useHistory } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import { setAssetBackLabel } from '@Actions/util.actions';
import { getTenantId } from '@innovyze/lib_get_service';
import { WEAVE_FONT } from '@Utils/fonts';
import { encodeRouteParam } from '@Utils/urlFormatting';
import { FdResultsInfoBanner } from './FdResultsInfoBanner';
import { renderDarkTooltipHeader } from './renderDarkTooltipHeader';

interface FailureDefinitionResultsTabProps {
	failureDefinition: FailureDefinition | null;
}

// Empty state component similar to the screenshot
const ResultsInProgressEmptyState: React.FC<{ isPolling: boolean }> = ({ isPolling }) => {
	const { t } = useGlobalization();

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			minHeight="400px"
			sx={{ textAlign: 'center' }}>
			{/* Box icon similar to screenshot */}
			<svg width="120" height="120" viewBox="0 0 120 120" style={{ marginBottom: '24px', opacity: 0.6 }}>
				{/* Main box body */}
				<rect x="15" y="30" width="90" height="70" fill="#f5f5f5" stroke="#ccc" strokeWidth="2" rx="4" />
				{/* Box lid */}
				<rect x="25" y="20" width="70" height="20" fill="#f5f5f5" stroke="#ccc" strokeWidth="2" rx="3" />
				{/* Box handle */}
				<rect x="50" y="15" width="20" height="8" fill="none" stroke="#ccc" strokeWidth="2" rx="4" />
				{/* Package symbol */}
				<line x1="30" y1="50" x2="90" y2="50" stroke="#ddd" strokeWidth="1" />
				<line x1="60" y1="35" x2="60" y2="85" stroke="#ddd" strokeWidth="1" />
			</svg>
			<Text variant="heading-medium-strong" sx={{ mb: 1 }}>
				{t('Results calculation in progress')}
			</Text>
			<Text variant="body1" sx={{ color: 'text.secondary' }}>
				{isPolling
					? t('Calculation might take some time. We will notify you when ready.')
					: t('No results available for this failure definition.')}
			</Text>
		</Box>
	);
};

export const FailureDefinitionResultsTab: React.FC<FailureDefinitionResultsTabProps> = ({ failureDefinition }) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const failureDefinitionRunResults = selectFailureDefinitionRunResults();
	const failureDefinitionId = failureDefinition?.id || '';
	const runId = failureDefinition?.lastRunId || '';
	const systemType = failureDefinition?.systemType || '';
	const assetType = failureDefinition?.assetType || '';

	// The FD run stores the db asset-type id (e.g. "wwPipe"); resolve its human-readable display name
	// (e.g. "Pipe") from the asset schema, falling back to the raw id while loading or on error.
	const { data: assetTypeSummary } = useAssetTypeSummary(assetType);
	const assetTypeDisplayName = assetTypeSummary?.displayName ? t(assetTypeSummary.displayName) : assetType;

	const storedPaginationState = selectFailureDefinitionModelPaginationState(failureDefinitionId);
	const defaultRequest: GetFailureDefinitionRunResultsRequest = {
		failureDefinitionId,
		runId,
		orderBy: 'assetId',
		order: 1, // 1 for ascending
		searchText: null,
		filters: null,
		filter_logistic_operator: 'and',
		page: 1,
		pageSize: 10,
		filterModel: { items: [] },
	};

	const [resultsRequest, setResultsRequest] = useState<GetFailureDefinitionRunResultsRequest>(
		storedPaginationState || defaultRequest,
	);

	const updateResultsRequest = (
		updater: (prev: GetFailureDefinitionRunResultsRequest) => GetFailureDefinitionRunResultsRequest,
	) => {
		setResultsRequest(prev => {
			const next = updater(prev);
			dispatch(
				setFailureDefinitionPaginationState({
					failureDefinitionId,
					paginationRequest: next,
				}),
			);
			return next;
		});
	};

	const [lastKnownTotal, setLastKnownTotal] = useState<number>(failureDefinitionRunResults.pagination.total || 0);

	useEffect(() => {
		if (!failureDefinitionRunResults.isLoading) {
			setLastKnownTotal(failureDefinitionRunResults.pagination.total || 0);
		}
	}, [failureDefinitionRunResults.isLoading, failureDefinitionRunResults.pagination.total]);

	useEffect(() => {
		if (failureDefinition?.id && failureDefinition?.lastRunId) {
			updateResultsRequest(prev => ({
				...prev,
				failureDefinitionId: failureDefinition.id || '',
				runId: failureDefinition.lastRunId || '',
			}));
		}
	}, [failureDefinition?.id, failureDefinition?.lastRunId]);

	const fetchResults = useCallback(
		(request: GetFailureDefinitionRunResultsRequest) => {
			dispatch(getFailureDefinitionRunResults(request));
		},
		[dispatch],
	);

	// Debounced version for search/filter changes
	const debouncedFetchResults = useCallback(
		debounce((request: GetFailureDefinitionRunResultsRequest) => {
			fetchResults(request);
		}, 300), // 300ms delay
		[fetchResults],
	);

	// Polling functionality to check for results availability
	useEffect(() => {
		if (!failureDefinition?.id) return undefined;

		const shouldPoll = () => {
			return failureDefinition.status === StatusType.inProgress;
		};

		if (shouldPoll()) {
			const pollInterval = setInterval(() => {
				dispatch(getFailureDefinitionAction(failureDefinition.id));
			}, 5000);

			return () => clearInterval(pollInterval);
		} else {
			if (failureDefinition.status === StatusType.complete) {
				const request: GetFailureDefinitionRunResultsRequest = {
					failureDefinitionId: failureDefinition.id,
					runId: failureDefinition.lastRunId ?? '',
					orderBy: resultsRequest.orderBy,
					order: resultsRequest.order,
					searchText: resultsRequest.searchText,
					filters: resultsRequest.filters,
					filter_logistic_operator: 'and' as const,
					page: resultsRequest.page,
					pageSize: resultsRequest.pageSize,
					filterModel: resultsRequest.filterModel,
				};
				dispatch(getFailureDefinitionRunResults(request));
			} else if (failureDefinition.status === StatusType.failed) {
				dispatch(error(t('Failure definition run failed')));
			}
			return undefined;
		}
	}, [failureDefinition?.id, failureDefinition?.lastRunId, failureDefinition?.status, dispatch]);

	useEffect(() => {
		if (!failureDefinition?.id || !failureDefinition?.lastRunId) {
			return;
		}

		const request: GetFailureDefinitionRunResultsRequest = {
			failureDefinitionId: failureDefinition.id,
			runId: failureDefinition.lastRunId,
			orderBy: resultsRequest.orderBy,
			order: resultsRequest.order,
			searchText: resultsRequest.searchText,
			filters: resultsRequest.filters,
			filter_logistic_operator: 'and' as const,
			page: resultsRequest.page,
			pageSize: resultsRequest.pageSize,
			filterModel: resultsRequest.filterModel,
		};

		// Determine if this is a search/filter change or pagination/sorting change
		const isSearchOrFilter = resultsRequest.searchText || resultsRequest.filters;

		if (isSearchOrFilter) {
			debouncedFetchResults(request);
		} else {
			fetchResults(request);
		}
	}, [
		// Core dependencies that should always trigger a call
		failureDefinition?.id,
		failureDefinition?.lastRunId,

		// Pagination/sorting (immediate)
		resultsRequest.page,
		resultsRequest.pageSize,
		resultsRequest.orderBy,
		resultsRequest.order,

		// Search/filter (debounced)
		resultsRequest.searchText,
		JSON.stringify(resultsRequest.filters),

		// Functions
		fetchResults,
		debouncedFetchResults,
	]);

	useEffect(() => {
		return () => {
			debouncedFetchResults.clear();
		};
	}, [debouncedFetchResults]);

	const handleRefresh = async () => {
		if (failureDefinition?.id) {
			// First, fetch the latest failure definition to get the current lastRunId
			dispatch(getFailureDefinitionAction(failureDefinition.id));
		}
	};

	const formatDate = (dateString: string) => {
		try {
			return new Date(dateString).toLocaleDateString('en-US');
		} catch {
			return dateString;
		}
	};

	// Shared column helpers — keep the DataGrid column definitions DRY (header tooltips + date columns repeat).
	// Args are already translated (t() stays at the call sites so i18next-parser can extract the keys).
	const darkHeader = (label: string, body: string) => (): JSX.Element => renderDarkTooltipHeader(label, label, body);

	const dateColumn = (field: string, headerName: string, tooltipBody: string): GridColDef => ({
		field,
		headerName,
		renderHeader: darkHeader(headerName, tooltipBody),
		flex: 1,
		type: 'date',
		filterable: true,
		align: 'left',
		headerAlign: 'left',
		valueGetter: ({ value }) => value && new Date(value),
		renderCell: params => (params.value ? formatDate(params.value) : '-'),
	});

	const hasActiveFilters = resultsRequest.searchText || resultsRequest.filters;

	// If we have no failure definition or no lastRunId, show the empty state
	if (!failureDefinition || !failureDefinition.lastRunId) {
		const isPolling = !failureDefinition || !failureDefinition.lastRunId;
		return <ResultsInProgressEmptyState isPolling={isPolling} />;
	}

	// If we're in the initial loading state (no results yet and still processing) AND no active filters
	if (
		failureDefinitionRunResults.rows.length === 0 &&
		failureDefinitionRunResults.pagination.total === 0 &&
		failureDefinitionRunResults.isLoading &&
		!hasActiveFilters
	) {
		return <ResultsInProgressEmptyState isPolling={true} />;
	}

	return (
		<Stack spacing={3}>
			<Stack spacing={0.5}>
				<Typography
					sx={{ fontFamily: WEAVE_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
					{t('Failure Definition Results')}
				</Typography>
				<Typography sx={{ fontFamily: WEAVE_FONT, fontSize: '0.8125rem', color: '#6b7280' }}>
					{t(
						'This list shows the earliest recorded instance of failure per asset. Each asset appears once, even if it experienced multiple failures. These failure signals are used to build failure-based deterioration models for asset groups.',
					)}
				</Typography>
			</Stack>
			<StylovyzeCard
				topText={`${systemType} · ${assetTypeDisplayName}`}
				topSubText={t('Have experienced failure')}
				countText={`${failureDefinitionRunResults.pagination.total}`}
				bottomTextLeft={t('Results below were created on {{date}}', {
					date: failureDefinition.lastRunAt ? formatDate(failureDefinition.lastRunAt) : t('Unknown date'),
				})}
			/>

			<FdResultsInfoBanner />

			<DataGrid
				weave
				loading={failureDefinitionRunResults.isLoading}
				skeletonRowCount={resultsRequest.pageSize}
				columns={[
					{
						field: 'assetId',
						headerName: t('Asset ID'),
						renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset ID')}</Text>,
						flex: 1,
						type: 'string',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => {
							return (
								<Link
									underline="hover"
									style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
									title={params.row.assetId}
									onClick={() => {
										dispatch(setAssetBackLabel(t('Back to Failure Definition')));
										history.push(
											addRouteNamespace(
												`asset-details/${encodeRouteParam(failureDefinition.assetType)}/${encodeRouteParam(
													params.row.assetId,
												)}`,
											),
										);
									}}>
									{params.row.assetId}
								</Link>
							);
						},
					},
					dateColumn('installationDate', t('Installation Date'), t('The date when this asset was installed or laid.')),
					{
						field: 'ageAtFailure',
						headerName: t('Age at Failure'),
						renderHeader: darkHeader(
							t('Age at Failure'),
							t('The age of the asset in years at the time of its earliest recorded failure.'),
						),
						flex: 1,
						type: 'number',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params =>
							params.value !== null && params.value !== undefined ? Math.floor(params.value) : '-',
					},
					dateColumn('failureDate', t('Failure Date'), t('The earliest recorded date this asset experienced failure.')),
					{
						field: 'failureType',
						headerName: t('Failure Type'),
						renderHeader: darkHeader(
							t('Failure Type'),
							t(
								'The type of failure event or condition that triggered this failure record, based on the configured parameters.',
							),
						),
						flex: 1,
						type: 'string',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => params.value || t('No Failure'),
					},
				]}
				disableColumnMenu
				disableColumnPinning
				disableColumnReorder
				disableRowSelectionOnClick
				// search and filter
				search={{
					value: resultsRequest.searchText || '',
					onChange: (searchValue: string) => {
						updateResultsRequest(prev => ({
							...prev,
							searchText: searchValue,
							page: 1, // Reset to first page when searching
						}));
					},
					onCancelSearch: () => {
						updateResultsRequest(prev => ({
							...prev,
							searchText: null,
							page: 1,
						}));
					},
					placeholder: t('Search by Asset ID, Failure Type...'),
				}}
				filterToolbar
				filterMode="server"
				filterModel={resultsRequest.filterModel as GridFilterModel}
				onFilterModelChange={model => {
					const noValueOperators = ['isEmpty', 'isNotEmpty'];
					const filters: Filter[] = (model.items ?? [])
						.filter(item => {
							if (!item.field) return false;
							if (noValueOperators.includes(item.operator)) return true;
							if (item.value === undefined || item.value === null) return false;
							if (Array.isArray(item.value) && item.value.length === 0) return false;
							if (item.value === '') return false;
							return true;
						})
						.map(item => ({
							field: item.field,
							operator: item.operator,
							value: Array.isArray(item.value)
								? item.value
								: item.value instanceof Date
								? item.value.toISOString().split('T')[0]
								: item.value?.toString() || '',
						}));

					updateResultsRequest(prev => ({
						...prev,
						filters: filters.length > 0 ? filters : null,
						filter_logistic_operator: model.logicOperator === GridLogicOperator.Or ? 'or' : 'and',
						filterModel: model,
						page: 1,
					}));
				}}
				slotProps={{
					filterPanel: {
						logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
					},
				}}
				// refresh
				refreshButton={{
					onRefresh: handleRefresh,
				}}
				// sorting
				sortingMode="server"
				sortModel={[
					{
						field: resultsRequest.orderBy,
						sort: resultsRequest.order === 1 ? 'asc' : 'desc',
					},
				]}
				onSortModelChange={model => {
					const orderBy = model[0]?.field || 'assetId';
					const order = model[0]?.sort === 'desc' ? -1 : 1;
					updateResultsRequest(prev => ({
						...prev,
						orderBy,
						order,
					}));
				}}
				// export
				export={{
					source: 'pipe.deterioration.export.create',
					total: failureDefinitionRunResults.pagination.total,
					requestParams: {
						export_type: 'failure-definition-export',
						failure_definition_id: resultsRequest.failureDefinitionId,
						run_id: resultsRequest.runId ?? '',
						tenant_id: getTenantId() || '',
						page: failureDefinitionRunResults.pagination.page.toString(),
						page_size: failureDefinitionRunResults.pagination.slice.toString(),
						order_by: resultsRequest.orderBy,
						order: resultsRequest.order as unknown as string,
						search_text: resultsRequest.searchText ?? '',
						filters: JSON.stringify(resultsRequest.filters),
						filter_logistic_operator: resultsRequest.filter_logistic_operator ?? '',
					},
				}}
				rows={(failureDefinitionRunResults.rows as unknown as FailureDefinitionRunResult[]).map(
					(result: FailureDefinitionRunResult, index: number) => ({
						id: result.id || index.toString(),
						...result,
					}),
				)}
				rowCount={
					failureDefinitionRunResults.isLoading
						? Math.max(lastKnownTotal, resultsRequest.page * resultsRequest.pageSize)
						: failureDefinitionRunResults.pagination.total
				}
				// pagination
				pagination
				pageSizeOptions={[10, 25, 50, 100]}
				paginationMode="server"
				paginationModel={{
					page: resultsRequest.page - 1,
					pageSize: resultsRequest.pageSize,
				}}
				onPaginationModelChange={model => {
					const nextPage = model.page + 1;
					const nextPageSize = model.pageSize;
					if (
						(failureDefinitionRunResults.isLoading || failureDefinitionRunResults.pagination.total === 0) &&
						nextPage < resultsRequest.page
					) {
						return;
					}
					updateResultsRequest(prev => ({
						...prev,
						page: nextPage,
						pageSize: nextPageSize,
					}));
				}}
			/>
		</Stack>
	);
};
